/**
 * Client CMR inbound bridge — config-driven projection for session initiation.
 * medrec-intake always returns the full CMR (regression / medrec shaping).
 *
 * TCIs with any active cmrDataFlow require a valid session-bound SecureLink
 * tci-session grant before CMR context is returned (including patient self-service).
 */

import firebaseApp, { db } from "services/firebase";
import { fetchPatientCMR } from "features/chartmind/components/chartmind/services/cmrReadService";
import {
  getActiveCmrDataFlow,
  projectCmrInboundFields,
  sectionsFromCmrDataFlow,
} from "constants/cmrFieldRegistry";
import {
  assertClientSessionCmrGrant,
  inboundSectionsForTci,
} from "services/cmr/tciSessionConsent";

/** Must match promptGraphRunnerContract.MEDREC_INTAKE_TCI_ID */
export const MEDREC_INTAKE_TCI_ID = "medrec-intake";

/**
 * Pure projection helper (exported for unit tests).
 */
export function buildCmrInboundContextFromDocs({ tciId, tciDoc, cmrData } = {}) {
  const id = typeof tciId === "string" ? tciId.trim() : "";
  if (id === MEDREC_INTAKE_TCI_ID) {
    return {
      applied: false,
      reason: "medrec-intake-bypass",
      context: cmrData && typeof cmrData === "object" ? cmrData : {},
      inbound: [],
    };
  }

  const flow = getActiveCmrDataFlow(tciDoc || {});
  if (!flow.inbound.length) {
    return {
      applied: false,
      reason: "no-inbound-config",
      context: cmrData && typeof cmrData === "object" ? cmrData : {},
      inbound: [],
    };
  }

  return {
    applied: true,
    reason: "projected",
    context: projectCmrInboundFields(cmrData || {}, flow.inbound),
    inbound: flow.inbound,
  };
}

async function loadTciDoc(tciId) {
  const id = typeof tciId === "string" ? tciId.trim() : "";
  if (!id) return null;
  try {
    const snap = await db.collection("tciRegistry").doc(id).get();
    if (!snap.exists) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    console.warn("[cmrInboundBridge] tciRegistry read failed", id, err);
    return null;
  }
}

/**
 * Fetch patient CMR and apply active inbound config for the TCI.
 * Missing CMR / fields / registry / consent → empty; never throws.
 *
 * @param {{
 *   tciId?: string,
 *   patientUid: string,
 *   tciSessionId?: string,
 *   readerUid?: string,
 *   cmrData?: object|null,
 * }} args
 * @returns {Promise<object>} context object for session / prompt-graph cmr node
 */
export async function fetchCmrWithInboundBridge({
  tciId,
  patientUid,
  tciSessionId,
  readerUid,
  cmrData: prefetched = undefined,
} = {}) {
  const id = typeof tciId === "string" ? tciId.trim() : "";

  // medrec-intake: established path — do not require tci-session grant.
  if (id === MEDREC_INTAKE_TCI_ID) {
    let cmrData = prefetched;
    if (cmrData === undefined) {
      try {
        cmrData = patientUid ? await fetchPatientCMR(patientUid) : null;
      } catch (err) {
        console.warn("[cmrInboundBridge] fetchPatientCMR failed (medrec)", err);
        cmrData = null;
      }
    }
    const tciDoc = await loadTciDoc(tciId);
    const result = buildCmrInboundContextFromDocs({
      tciId,
      tciDoc,
      cmrData,
    });
    return result.context;
  }

  const tciDoc = await loadTciDoc(tciId);
  const flow = getActiveCmrDataFlow(tciDoc || {});
  const hasCmrDataFlow =
    (flow.inbound?.length || 0) + (flow.outbound?.length || 0) > 0;

  if (hasCmrDataFlow) {
    const reader =
      (typeof readerUid === "string" && readerUid.trim()) ||
      firebaseApp.auth()?.currentUser?.uid ||
      (typeof patientUid === "string" ? patientUid.trim() : "");
    const sessionId =
      typeof tciSessionId === "string" ? tciSessionId.trim() : "";
    const inboundSections = inboundSectionsForTci(tciDoc);
    const requiredSections =
      inboundSections.length > 0
        ? inboundSections
        : sectionsFromCmrDataFlow(tciDoc);

    const grant = await assertClientSessionCmrGrant({
      tciSessionId: sessionId,
      readerUid: reader,
      patientUid,
      requiredSections,
    });

    if (!grant.ok) {
      console.warn(
        "[cmrInboundBridge] session CMR grant required; degrading to empty context",
        grant.reason,
      );
      return {};
    }

    if (!flow.inbound.length) {
      return {};
    }

    let cmrData = prefetched;
    if (cmrData === undefined) {
      try {
        cmrData = patientUid
          ? await fetchPatientCMR(patientUid, { sessionId })
          : null;
      } catch (err) {
        console.warn("[cmrInboundBridge] fetchPatientCMR failed", err);
        cmrData = null;
      }
    }

    const result = buildCmrInboundContextFromDocs({
      tciId,
      tciDoc,
      cmrData,
    });
    return result.context;
  }

  // No cmrDataFlow: backward-compatible full CMR (unchanged).
  let cmrData = prefetched;
  if (cmrData === undefined) {
    try {
      cmrData = patientUid ? await fetchPatientCMR(patientUid) : null;
    } catch (err) {
      console.warn("[cmrInboundBridge] fetchPatientCMR failed", err);
      cmrData = null;
    }
  }

  const result = buildCmrInboundContextFromDocs({
    tciId,
    tciDoc,
    cmrData,
  });
  return result.context;
}
